import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
} from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertTriangle, AlertCircle, Heart, Loader2, Wand2 } from 'lucide-react';

interface HelpfulCategory {
  score: number;
  analysis: string;
}

interface HelpfulContentData {
  peopleFirst: HelpfulCategory;
  expertise: HelpfulCategory;
  satisfyingExperience: HelpfulCategory;
  depthAndValue: HelpfulCategory;
  contentQuality: {
    originality: number;
    comprehensiveness: number;
    readability: number;
    actionability: number;
  };
  averageScore: number;
  classification: string;
  topIssues: string[];
  recommendations: string[];
}

interface HelpfulContentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: HelpfulContentData | null;
  onFix: () => void;
  isFixing: boolean;
}

const getScoreColor = (score: number) => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-red-600';
};

const getScoreBg = (score: number) => {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-amber-500';
  return 'bg-red-500';
};

const ScoreRing = ({ score, label, size = 'md' }: { score: number; label: string; size?: 'lg' | 'md' }) => {
  const radius = size === 'lg' ? 42 : 28;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const svgSize = size === 'lg' ? 100 : 68;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: svgSize, height: svgSize }}>
        <svg width={svgSize} height={svgSize} className="-rotate-90">
          <circle cx={svgSize / 2} cy={svgSize / 2} r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={size === 'lg' ? 6 : 4} />
          <circle cx={svgSize / 2} cy={svgSize / 2} r={radius} fill="none"
            stroke={score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444'}
            strokeWidth={size === 'lg' ? 6 : 4} strokeDasharray={circumference} strokeDashoffset={circumference - progress} strokeLinecap="round" className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-bold ${getScoreColor(score)} ${size === 'lg' ? 'text-xl' : 'text-sm'}`}>{score}</span>
        </div>
      </div>
      <span className={`text-muted-foreground font-medium ${size === 'lg' ? 'text-sm' : 'text-xs'}`}>{label}</span>
    </div>
  );
};

const CategoryCard = ({ name, icon, data }: { name: string; icon: React.ReactNode; data: HelpfulCategory }) => (
  <div className="rounded-lg border p-3 space-y-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-semibold">{name}</span>
      </div>
      <Badge variant={data.score >= 80 ? 'default' : data.score >= 60 ? 'secondary' : 'destructive'} className="text-xs">{data.score}/100</Badge>
    </div>
    <div className="h-2 rounded-full bg-muted overflow-hidden">
      <div className={`h-full rounded-full ${getScoreBg(data.score)} transition-all duration-1000`} style={{ width: `${data.score}%` }} />
    </div>
    <p className="text-xs text-muted-foreground">{data.analysis}</p>
  </div>
);

const QualityBar = ({ label, score }: { label: string; score: number }) => (
  <div className="space-y-1">
    <div className="flex justify-between items-center">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-xs font-semibold ${getScoreColor(score)}`}>{score}</span>
    </div>
    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
      <div className={`h-full rounded-full ${getScoreBg(score)} transition-all duration-1000`} style={{ width: `${score}%` }} />
    </div>
  </div>
);

export const HelpfulContentDialog: React.FC<HelpfulContentDialogProps> = ({ open, onOpenChange, data, onFix, isFixing }) => {
  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitleWithBug popupName="Helpful Content Checker" popupContext="/articles" isOpen={open}>
            <div className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-primary" />
              Help-Full Content Check
            </div>
          </DialogTitleWithBug>
          <DialogDescription>Google's Helpful Content guidelines evaluation</DialogDescription>
        </DialogHeader>

        {/* Overall Score - Fixed on top */}
        <div className="flex items-center gap-6 p-4 rounded-xl border bg-gradient-to-r from-muted/50 to-muted/20">
          <div className="flex items-center gap-4 shrink-0">
            <ScoreRing score={data.averageScore} label="Overall" size="lg" />
            <div>
              <h3 className="text-lg font-bold">Score</h3>
              <Badge variant={data.averageScore >= 80 ? 'default' : data.averageScore >= 60 ? 'secondary' : 'destructive'}>{data.classification}</Badge>
            </div>
          </div>
          <div className="flex gap-3 ml-auto">
            <ScoreRing score={data.peopleFirst.score} label="People" />
            <ScoreRing score={data.expertise.score} label="Expert" />
            <ScoreRing score={data.satisfyingExperience.score} label="Satisfy" />
            <ScoreRing score={data.depthAndValue.score} label="Depth" />
          </div>
        </div>

        <ScrollArea className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 280px)' }}>
          <div className="space-y-5 p-1">
            {/* Category Breakdown */}
            <div className="grid grid-cols-2 gap-3">
              <CategoryCard name="People-First" icon={<Heart className="h-4 w-4 text-pink-500" />} data={data.peopleFirst} />
              <CategoryCard name="Expertise" icon={<CheckCircle className="h-4 w-4 text-purple-500" />} data={data.expertise} />
              <CategoryCard name="Satisfying Experience" icon={<CheckCircle className="h-4 w-4 text-blue-500" />} data={data.satisfyingExperience} />
              <CategoryCard name="Depth & Value" icon={<CheckCircle className="h-4 w-4 text-orange-500" />} data={data.depthAndValue} />
            </div>

            {/* Content Quality */}
            <div className="rounded-lg border p-4 space-y-3">
              <h4 className="text-sm font-semibold">Content Quality Metrics</h4>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <QualityBar label="Originality" score={data.contentQuality.originality} />
                <QualityBar label="Comprehensiveness" score={data.contentQuality.comprehensiveness} />
                <QualityBar label="Readability" score={data.contentQuality.readability} />
                <QualityBar label="Actionability" score={data.contentQuality.actionability} />
              </div>
            </div>

            {/* Issues */}
            {data.topIssues?.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 p-4 space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />Top Issues
                </h4>
                <ul className="space-y-1.5">
                  {data.topIssues.map((issue, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span>{issue}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.recommendations?.length > 0 && (
              <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-900 p-4 space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />Recommendations
                </h4>
                <ul className="space-y-1.5">
                  {data.recommendations.map((rec, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-2"><span className="text-green-400 mt-0.5">•</span>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Fix Button */}
            {data.averageScore >= 80 ? (
              <div className="rounded-lg border-2 border-dashed border-green-500/30 p-4 flex items-center justify-between bg-green-50 dark:bg-green-950/20">
                <div>
                  <h4 className="text-sm font-semibold">🎉 Nothing to fix here!</h4>
                  <p className="text-xs text-muted-foreground">Your content is already crushing it — Google would swipe right on this article.</p>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-primary/30 p-4 flex items-center justify-between bg-primary/5">
                <div>
                  <h4 className="text-sm font-semibold">Want me to sprinkle some magic? ✨</h4>
                  <p className="text-xs text-muted-foreground">Your article has potential — let AI give it the glow-up it deserves.</p>
                </div>
                <Button onClick={onFix} disabled={isFixing} className="gap-2">
                  {isFixing ? (<><Loader2 className="h-4 w-4 animate-spin" />Rewriting...</>) : (<><Wand2 className="h-4 w-4" />Fix Article</>)}
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
